import React from 'react';
import { getProjectPriorityInfo } from '../../utils/projectPriority';

const MeetingHistoryModal = ({
  isOpen,
  onClose,
  meetingHistory,
  meetingHistoryLoading,
  meetingHistoryError,
  circleName,
}) => {
  if (!isOpen) return null;
  const safeHistory = Array.isArray(meetingHistory) ? meetingHistory : [];
  const pointStatusLabel = (status) => {
    const norm = (status || 'open').toLowerCase();
    if (norm === 'treated' || norm === 'closed') return 'Traité';
    if (norm === 'converted') return 'Transformé en tension';
    return 'Ouvert';
  };
  const formatDateTime = (value) => (value ? new Date(value).toLocaleString('fr-FR') : '');
  const formatDate = (value) => (value ? new Date(value).toLocaleDateString('fr-FR') : '');
  const meetingTypeLabel = (type) => {
    const normalized = (type || 'gouvernance').toLowerCase();
    if (normalized === 'tactique') return 'Tactique';
    if (normalized === 'pilotage') return 'Pilotage';
    return 'Gouvernance';
  };
  // Même échelle que le reste de l'application (P0 = crise … P7 = pause).
  const priorityInfo = (value) => getProjectPriorityInfo(value);
  const reviewStatusLabel = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'achieved') return 'Atteint';
    if (normalized === 'partial') return 'Partiel';
    if (normalized === 'missed') return 'Non atteint';
    if (normalized === 'unknown') return 'Non statué';
    return 'Non renseigné';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
        <button
          className="absolute top-3 right-3 rounded-full p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100"
          onClick={onClose}
          aria-label="Fermer"
        >
          &#10005;
        </button>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Historique des réunions</p>
            <span className="text-xs text-slate-500">{circleName || 'Cercle'}</span>
          </div>
          {meetingHistoryLoading ? (
            <div className="text-sm text-slate-400">Chargement...</div>
          ) : meetingHistoryError ? (
            <div className="text-sm text-red-500">{meetingHistoryError}</div>
          ) : safeHistory.length === 0 ? (
            <div className="text-sm text-slate-400">Aucune réunion clôturée.</div>
          ) : (
            <div className="space-y-4">
              {safeHistory.map((entry) => {
                const meeting = entry?.meeting || {};
                const points = Array.isArray(entry?.points) ? entry.points : [];
                const projectReviews = Array.isArray(entry?.project_reviews) ? entry.project_reviews : [];
                const notes = entry?.notes || {};
                const attendees = Array.isArray(entry?.participants) ? entry.participants : [];
                const notesContent = (notes?.content || '').trim();
                const notesUpdatedAt = notes?.updated_at;
                const pointNotesCount = points.filter((point) => (point?.notes || '').trim().length > 0).length;
                const statusCounts = points.reduce(
                  (acc, point) => {
                    const norm = String(point?.status || 'open').toLowerCase();
                    if (norm === 'treated') acc.treated += 1;
                    else if (norm === 'converted') acc.converted += 1;
                    else if (norm === 'closed') acc.treated += 1;
                    else acc.open += 1;
                    return acc;
                  },
                  { open: 0, treated: 0, converted: 0 }
                );
                const actionCounts = points.reduce(
                  (acc, point) => {
                    const actions = Array.isArray(point?.actions) ? point.actions : [];
                    actions.forEach((action) => {
                      const type = String(action?.type || '').toLowerCase();
                      if (type === 'objective') acc.objective += 1;
                      else if (type === 'task') acc.task += 1;
                      else acc.other += 1;
                    });
                    return acc;
                  },
                  { task: 0, objective: 0, other: 0 }
                );
                const totalActions = actionCounts.task + actionCounts.objective + actionCounts.other;
                const closedAt = meeting.closed_at || meeting.updated_at || meeting.created_at;
                const typeLabel = meetingTypeLabel(meeting.type);
                return (
                  <div key={meeting.id} className="border border-slate-200 rounded-xl p-4 bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-slate-800">
                        Réunion {typeLabel}
                      </div>
                      <div className="text-xs text-slate-500">{formatDateTime(closedAt)}</div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Points</div>
                        <div className="text-sm font-semibold text-slate-800">{points.length}</div>
                        <div className="text-[11px] text-slate-500">
                          Traités {statusCounts.treated} · Transformés en tension {statusCounts.converted}
                          {statusCounts.open > 0 ? ` · Ouverts ${statusCounts.open}` : ''}
                        </div>
                      </div>
                      <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Actions</div>
                        <div className="text-sm font-semibold text-slate-800">{totalActions}</div>
                        <div className="text-[11px] text-slate-500">
                          Tâches {actionCounts.task} · Objectifs {actionCounts.objective}
                        </div>
                      </div>
                      <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Notes</div>
                        <div className="text-sm font-semibold text-slate-800">
                          {notesContent ? 'Partagées' : 'Aucune'}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {pointNotesCount} point{pointNotesCount > 1 ? 's' : ''} avec notes
                        </div>
                      </div>
                    </div>
                    {projectReviews.length > 0 && (
                      <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 mb-3">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pilotage projets</p>
                          <span className="text-[10px] text-slate-400">{projectReviews.length}</span>
                        </div>
                        <div className="mt-2 space-y-2">
                          {projectReviews.map((review) => {
                            const priority = priorityInfo(review.priority);
                            return (
                              <div key={`${review.project_id}-${review.id}`} className="rounded-lg border border-slate-200 bg-white p-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div className="text-sm font-semibold text-slate-800">{review.project_name || 'Projet'}</div>
                                  {priority && (
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${priority.className}`}>
                                      P{priority.value} - {priority.label}
                                    </span>
                                  )}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-1">
                                  Objectif précédent : {reviewStatusLabel(review.previous_status)}
                                </div>
                                {review.next_milestone && (
                                  <p className="text-xs text-slate-600 mt-2">Jalon : {review.next_milestone}</p>
                                )}
                                {review.weekly_objective && (
                                  <p className="text-xs text-slate-600 mt-2 whitespace-pre-wrap">
                                    Objectif : {review.weekly_objective}
                                  </p>
                                )}
                                {Array.isArray(review.news) && review.news.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-xs font-semibold text-slate-600">Quoi de neuf</p>
                                    <ul className="mt-1 space-y-0.5">
                                      {review.news.map((item, index) => (
                                        <li key={index} className="text-xs text-slate-600">• {item}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {review.notes && (
                                  <p className="text-xs text-slate-500 mt-2 whitespace-pre-wrap">
                                    Notes : {review.notes}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 mb-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Notes de réunion</p>
                        {notesUpdatedAt && (
                          <span className="text-[10px] text-slate-400">Mises à jour le {formatDate(notesUpdatedAt)}</span>
                        )}
                      </div>
                      {notesContent ? (
                        <p className="text-xs text-slate-600 mt-2 whitespace-pre-wrap">{notesContent}</p>
                      ) : (
                        <p className="text-xs text-slate-400 italic mt-2">Aucune note partagée.</p>
                      )}
                    </div>
                    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 mb-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Participants</p>
                        <span className="text-[10px] text-slate-400">{attendees.length}</span>
                      </div>
                      {attendees.length === 0 ? (
                        <p className="text-xs text-slate-400 italic mt-2">Aucune présence enregistrée.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {attendees.map((participant) => (
                            <span
                              key={`${participant.id}-${participant.joined_at || ''}`}
                              className="text-[11px] px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-600"
                            >
                              {participant.name || 'Membre inconnu'}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {points.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">Aucun point enregistré.</p>
                    ) : (
                      <div className="space-y-3">
                        {points.map((point) => {
                          const actions = Array.isArray(point.actions) ? point.actions : [];
                          return (
                            <div key={point.id} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                              <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-semibold text-slate-800">{point.title}</div>
                                <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">
                                  {pointStatusLabel(point.status)}
                                </span>
                              </div>
                              <div className="text-[11px] text-slate-500 mt-1">
                                Par {point.author_name || 'un membre'}
                              </div>
                              {Array.isArray(point.summary) && point.summary.length > 0 && (
                                <ul className="mt-2 space-y-0.5">
                                  {point.summary.map((item, index) => (
                                    <li key={index} className="text-xs text-slate-600">• {item}</li>
                                  ))}
                                </ul>
                              )}
                              {point.notes && (
                                <p className="text-xs text-slate-600 mt-2 whitespace-pre-wrap">{point.notes}</p>
                              )}
                              {actions.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {actions.map((action, idx) => (
                                    <div key={`${point.id}-action-${idx}`} className="text-xs text-slate-500">
                                      {(action?.type || '').toLowerCase() === 'objective'
                                        ? 'Objectif'
                                        : (action?.type || '').toLowerCase() === 'task'
                                          ? 'Tâche'
                                          : 'Action'} : {action?.name || 'Sans titre'}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingHistoryModal;
